/* =========================================================================
   Shared visual furniture for the auth screens (login + register).

   The sign-in screen read as "stale / old" (Eric 2026-08-21): bare inputs on
   the raw background, a glyph for a crest, and none of the candlelit warmth
   the home screen already has. Eric's follow-up reference set (a game HUD, a
   nav bar, a bevelled greeting card) pins the direction — chunky rounded
   shapes with a visible lighter rim, circular icon buttons, soft FILLED
   glyphs rather than hairline strokes, and a glow ring on whatever is live.
   Hairline-stroke icons are specifically what made it read as generic.

   And Brother Theo now stands on the login screen: he is the app's face, the
   art already exists (media/characters/theo-welcome.png), and a character
   greeting is the one thing a form can't fake.

   Markup + CSS only, except wirePasswordReveals(), which is additive. No ids
   move, so auth.ts's byId() lookups and setMsg()'s className contract on
   .auth-msg are untouched.
   ========================================================================= */

/** The candlelit wash behind an auth screen: two slow-drifting light pools and
    a few specks. Decorative — aria-hidden, pointer-events:none, and stilled by
    prefers-reduced-motion in CSS (it's atmosphere, not information). */
export const authBackdropHTML = /* html */ `
<div class="auth-bg" aria-hidden="true">
  <span class="auth-pool a"></span>
  <span class="auth-pool b"></span>
  <i class="auth-speck s1"></i>
  <i class="auth-speck s2"></i>
  <i class="auth-speck s3"></i>
</div>`;

/**
 * Brother Theo, standing in his own pool of light with a word for the player.
 *
 * The art is probed the same graceful way gtky.ts and roundPicker.ts do it:
 * the block renders collapsed and only reveals once the PNG actually decodes,
 * so a missing or slow asset costs nothing and never shifts the form under a
 * finger that is already reaching for it.
 */
export function theoStageHTML(line: string): string {
  // Class, not id: login and register BOTH mount a stage, and a duplicate id
  // would make document.getElementById reach only whichever came first.
  return (
    `<div class="theo-stage" aria-hidden="true">` +
    `<div class="theo-halo"></div>` +
    `<img class="theo-art" alt="" />` +
    `<div class="theo-say"><span>${line}</span></div>` +
    `</div>`
  );
}

/** Brother Theo's art for the auth screens — his open-armed welcome pose. */
export const THEO_WELCOME_ART = 'media/characters/theo-welcome.png';

/**
 * Reveal Brother Theo on every auth screen once his art has decoded.
 *
 * Probes once per session (module latch) and then fills every `.theo-stage`
 * on the page — both auth screens share the pose. Until it resolves the stage
 * stays collapsed, so a missing or slow PNG costs nothing and never shifts the
 * form under a finger already reaching for it. Safe with no Image() (tests).
 */
let theoProbed = false;

export function revealTheo(src: string = THEO_WELCOME_ART): void {
  if (theoProbed) return;
  theoProbed = true;
  const fill = (): void => {
    document.querySelectorAll<HTMLElement>('.theo-stage').forEach((stage) => {
      const img = stage.querySelector<HTMLImageElement>('.theo-art');
      if (img && !img.src) img.src = src;
      stage.classList.add('ready');
    });
  };
  try {
    const probe = new Image();
    probe.onload = fill;
    probe.onerror = () => {
      /* art not present — every stage stays collapsed, forms are unaffected */
    };
    probe.src = src;
  } catch {
    /* no Image() constructor (tests) — stages stay collapsed */
  }
}

/** Test seam: forget the probe latch so a fresh probe can run. */
export function resetTheoProbe(): void {
  theoProbed = false;
}

/* --- Field icons. Soft FILLED glyphs, per the reference set: they read as
       solid little objects at 18px where hairline strokes turn to mush, and
       they inherit currentColor so the whole field can warm up on focus. --- */
const SVG = (body: string): string =>
  `<svg class="input-ic" viewBox="0 0 18 18" aria-hidden="true" focusable="false" fill="currentColor">${body}</svg>`;

export const ICON_EMAIL = SVG(
  `<path d="M4.2 4h9.6A2.2 2.2 0 0 1 16 6.2v5.6A2.2 2.2 0 0 1 13.8 14H4.2A2.2 2.2 0 0 1 2 11.8V6.2A2.2 2.2 0 0 1 4.2 4Z" opacity=".45"/>` +
    `<path d="M2.4 5.5a2.2 2.2 0 0 1 1.8-1.5h9.6a2.2 2.2 0 0 1 1.8 1.5L9.8 9.2a1.6 1.6 0 0 1-1.6 0Z"/>`,
);
export const ICON_LOCK = SVG(
  `<path d="M5.6 7.9h6.8A2.3 2.3 0 0 1 14.7 10.2v2.6A2.3 2.3 0 0 1 12.4 15H5.6a2.3 2.3 0 0 1-2.3-2.2v-2.6A2.3 2.3 0 0 1 5.6 7.9Z"/>` +
    `<path d="M9 2.4a3.4 3.4 0 0 1 3.4 3.4v2.3h-1.9V5.8a1.5 1.5 0 0 0-3 0v2.3H5.6V5.8A3.4 3.4 0 0 1 9 2.4Z" opacity=".55"/>`,
);
export const ICON_USER = SVG(
  `<circle cx="9" cy="5.7" r="2.9"/>` +
    `<path d="M9 9.5c-3.1 0-5.5 1.9-5.5 4.3 0 .7.5 1.2 1.2 1.2h8.6c.7 0 1.2-.5 1.2-1.2 0-2.4-2.4-4.3-5.5-4.3Z" opacity=".55"/>`,
);

const EYE = `<path d="M9 4C4.8 4 2 8.2 1.7 8.6a.7.7 0 0 0 0 .8C2 9.8 4.8 14 9 14s7-4.2 7.3-4.6a.7.7 0 0 0 0-.8C16 8.2 13.2 4 9 4Zm0 7.6a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2Z"/>`;
const SLASH = `<path d="M3.1 2.4 15.6 14.9a.9.9 0 0 1-1.3 1.3L1.8 3.7a.9.9 0 0 1 1.3-1.3Z"/>`;

/** The show/hide control that sits inside a password field. */
function pwToggleHTML(id: string): string {
  const ic = (cls: string, body: string): string =>
    `<svg class="pw-ic ${cls}" viewBox="0 0 18 18" aria-hidden="true" focusable="false" fill="currentColor">${body}</svg>`;
  return (
    `<button type="button" class="pw-toggle" id="${id}" aria-label="Show password" aria-pressed="false">` +
    ic('pw-ic-on', EYE) +
    ic('pw-ic-off', EYE + SLASH) +
    `</button>`
  );
}

/**
 * One labelled field, icon inside the control. `inputHTML` is the caller's own
 * `<input>` — every id, name, autocomplete and validation attribute stays
 * exactly where it was, so this is a purely visual wrapper.
 */
export function authField(opts: {
  id: string;
  label: string;
  icon: string;
  inputHTML: string;
  /** id for the reveal button; its presence adds the show/hide control */
  pwToggleId?: string;
  /** extra markup inside the wrap (register's availability tick) */
  extraHTML?: string;
  /** markup after the wrap (register's availability hint line) */
  afterHTML?: string;
}): string {
  return (
    `<div class="field">` +
    `<label for="${opts.id}">${opts.label}</label>` +
    `<div class="input-wrap${opts.pwToggleId ? ' has-toggle' : ''}${opts.extraHTML ? ' has-avail' : ''}">` +
    opts.icon +
    opts.inputHTML +
    (opts.extraHTML ?? '') +
    (opts.pwToggleId ? pwToggleHTML(opts.pwToggleId) : '') +
    `</div>` +
    (opts.afterHTML ?? '') +
    `</div>`
  );
}

/**
 * Wire every password reveal on the page. Idempotent — already-wired buttons
 * are skipped — so it's safe from wireAuth() regardless of mount order.
 *
 * Deliberately flips only `type`: the value, selection and autocomplete
 * behaviour stay the browser's. Swapping the input node (the other common
 * approach) breaks password managers mid-fill.
 */
export function wirePasswordReveals(root: Document | HTMLElement = document): void {
  root.querySelectorAll<HTMLButtonElement>('.pw-toggle').forEach((btn) => {
    if (btn.dataset.wired === '1') return;
    btn.dataset.wired = '1';
    btn.addEventListener('click', () => {
      const wrap = btn.closest('.input-wrap');
      const input = wrap ? wrap.querySelector<HTMLInputElement>('input') : null;
      if (!input) return;
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.setAttribute('aria-pressed', show ? 'true' : 'false');
      btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
      // Flipping type parks the caret at 0 — put it back at the end.
      try {
        const end = input.value.length;
        input.setSelectionRange(end, end);
      } catch {
        /* setSelectionRange throws on some input types — harmless */
      }
    });
  });
}
